import { Fragment, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, ChevronRight, Download, Search } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { useAuthStore } from '../../store/authStore'
import { useCurrency } from '../../hooks/useCurrency'
import type { PageMeta } from '../../lib/permissions/pageMeta'
import { PageHeader } from '../../components/ui/PageHeader'
import { Spinner } from '../../components/ui/Spinner'
import { EmptyState } from '../../components/ui/EmptyState'
import { downloadCsv } from '../../lib/reportCsv'
import { downloadBlob } from '../../lib/staffTemplate/generate'
import { displayDate, today } from '../../utils/dates'
import { cn } from '../../utils/cn'
import toast from 'react-hot-toast'

// Product Purchase Report — one row per product purchased over a period, expandable
// to each purchase (with the supplier it was "bought from"). Reads the additive,
// read-only RPCs get_product_purchase_report / _detail / _detail_all (mig 390).
// Cost/spend side (no profit/margin). Mirror of the Product Sales Report.

export const pageMeta: PageMeta = {
  key: 'trading.product_purchases',
  label: 'Product Purchases',
  module: 'Trading',
  section: 'Reports',
  description: 'What you bought — per product, expandable to each purchase',
  defaultPermissions: {
    owner: 'view', admin: 'view', manager: 'view',
    accountant: 'none', cashier: 'none', viewer: 'none',
  },
}

type Row = {
  product_id: string
  product_name: string
  sku: string | null
  qty: number
  gross: number
  discount: number
  net: number
  vat: number
  total_incl: number
}
type Totals = Omit<Row, 'product_id' | 'product_name' | 'sku'>
type Report = { rows: Row[]; totals: Totals; from: string; to: string }

type DetailRow = {
  kind: 'purchase' | 'return'
  occurred_at: string
  bought_from: string
  qty: number
  unit_price: number | null
  discount: number
  net: number
  vat: number
  total_incl: number
  reference: string | null
  supplier_id: string | null
  purchase_id: string | null
}

type Preset = 'today' | 'week' | 'month' | 'half' | 'year' | 'custom'
type SortKey = 'net' | 'qty'

const PRESETS: { value: Preset; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This week' },
  { value: 'month', label: 'This month' },
  { value: 'half', label: 'This half-year' },
  { value: 'year', label: 'This year' },
  { value: 'custom', label: 'Custom' },
]

function pad(n: number): string { return String(n).padStart(2, '0') }
function resolveRange(p: Preset, customFrom: string, customTo: string): { from: string; to: string } {
  const now = new Date(); const y = now.getFullYear(); const mo = now.getMonth(); const d = now.getDate(); const t = today()
  switch (p) {
    case 'today': return { from: t, to: t }
    case 'week': {
      const back = (now.getDay() + 6) % 7
      const mon = new Date(y, mo, d - back)
      return { from: `${mon.getFullYear()}-${pad(mon.getMonth() + 1)}-${pad(mon.getDate())}`, to: t }
    }
    case 'month': return { from: `${y}-${pad(mo + 1)}-01`, to: t }
    case 'half': return { from: mo < 6 ? `${y}-01-01` : `${y}-07-01`, to: t }
    case 'year': return { from: `${y}-01-01`, to: t }
    case 'custom': {
      const from = customFrom || customTo || t; const to = customTo || customFrom || t
      return { from, to }
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const rpc = (fn: string, args: Record<string, unknown>) => (supabase as any).rpc(fn, args)

function fmtDateTime(iso: string): string {
  const [d, t] = iso.split('T')
  return t && t.slice(0, 5) !== '00:00' ? `${d} ${t.slice(0, 5)}` : d
}

export default function ProductPurchaseReportPage() {
  const { tenant } = useAuthStore()
  const { format } = useCurrency()
  const tenantId = tenant?.id ?? ''

  const [preset, setPreset] = useState<Preset>('month')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'net', dir: 'desc' })
  const [expanded, setExpanded] = useState<string | null>(null)
  const [downloading, setDownloading] = useState(false)

  const range = useMemo(() => resolveRange(preset, customFrom, customTo), [preset, customFrom, customTo])

  const { data, isLoading, isError } = useQuery<Report>({
    queryKey: ['product-purchase-report', tenantId, range.from, range.to],
    enabled: !!tenantId,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await rpc('get_product_purchase_report', {
        p_tenant_id: tenantId, p_from: range.from, p_to: range.to,
      })
      if (error) throw error
      return data as Report
    },
  })

  // detail_all powers the supplier-based summary band (suppliers count, top
  // suppliers, daily spend) AND the grouped "Download with suppliers" export.
  const { data: detailAll } = useQuery<{ rows: (DetailRow & { product_id: string })[] }>({
    queryKey: ['product-purchase-detail-all', tenantId, range.from, range.to],
    enabled: !!tenantId,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await rpc('get_product_purchase_detail_all', {
        p_tenant_id: tenantId, p_from: range.from, p_to: range.to,
      })
      if (error) throw error
      return data as { rows: (DetailRow & { product_id: string })[] }
    },
  })

  const rows = useMemo(() => {
    const all = data?.rows ?? []
    const q = search.trim().toLowerCase()
    const filtered = q
      ? all.filter(r => r.product_name.toLowerCase().includes(q) || (r.sku ?? '').toLowerCase().includes(q))
      : all
    return [...filtered].sort((a, b) => {
      const av = a[sort.key] ?? 0, bv = b[sort.key] ?? 0
      return sort.dir === 'desc' ? bv - av : av - bv
    })
  }, [data, search, sort])

  const totals = data?.totals

  // Summary band --------------------------------------------------------------
  const kpis = useMemo(() => ({
    spend: totals?.net ?? 0,
    qty: totals?.qty ?? 0,
    products: (data?.rows ?? []).length,
    suppliers: new Set((detailAll?.rows ?? []).map(r => r.bought_from)).size,
  }), [totals, data, detailAll])

  const topProducts = useMemo(
    () => [...(data?.rows ?? [])].sort((a, b) => b.net - a.net).slice(0, 5),
    [data],
  )
  const topSuppliers = useMemo(() => {
    const m = new Map<string, number>()
    for (const r of detailAll?.rows ?? []) m.set(r.bought_from, (m.get(r.bought_from) ?? 0) + r.net)
    return Array.from(m.entries()).map(([name, net]) => ({ name, net })).sort((a, b) => b.net - a.net).slice(0, 5)
  }, [detailAll])
  const trend = useMemo(() => {
    const m = new Map<string, number>()
    for (const r of detailAll?.rows ?? []) {
      const day = r.occurred_at.split('T')[0]
      m.set(day, (m.get(day) ?? 0) + r.net)
    }
    return Array.from(m.entries()).map(([day, net]) => ({ day, net })).sort((a, b) => (a.day < b.day ? -1 : 1))
  }, [detailAll])
  const trendMax = useMemo(() => Math.max(1, ...trend.map(d => Math.abs(d.net))), [trend])

  const toggleSort = (key: SortKey) =>
    setSort(s => (s.key === key ? { key, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: 'desc' }))

  const exportCsv = () => {
    if (!rows.length) return
    downloadCsv(
      `product-purchases-${range.from}_to_${range.to}`,
      ['Product', 'SKU', 'Qty', 'Gross cost', 'Discount', 'Net cost', 'VAT', 'Total (incl VAT)'],
      rows.map(r => [r.product_name, r.sku ?? '', r.qty, r.gross, r.discount, r.net, r.vat, r.total_incl]),
    )
  }

  const exportWithSuppliers = async () => {
    if (!rows.length || downloading) return
    setDownloading(true)
    try {
      const detailRows = detailAll?.rows ?? (await (async () => {
        const { data, error } = await rpc('get_product_purchase_detail_all', {
          p_tenant_id: tenantId, p_from: range.from, p_to: range.to,
        })
        if (error) throw error
        return (data as { rows: (DetailRow & { product_id: string })[] }).rows
      })())
      const byProduct = new Map<string, (DetailRow & { product_id: string })[]>()
      for (const dr of detailRows) {
        const arr = byProduct.get(dr.product_id)
        if (arr) arr.push(dr); else byProduct.set(dr.product_id, [dr])
      }

      const ExcelJS = (await import('exceljs')).default
      const wb = new ExcelJS.Workbook()
      const ws = wb.addWorksheet('Product purchases', { views: [{ state: 'frozen', ySplit: 1 }] })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(ws.properties as any).outlineProperties = { summaryBelow: false, summaryRight: false }

      const headers = ['Product / Date', 'SKU / Bought from', 'Qty', 'Gross / Unit cost', 'Discount', 'Net', 'VAT', 'Total incl VAT', 'Ref']
      ws.addRow(headers).font = { bold: true }
      const MONEY = '#,##0.00'
      const moneyCols = [4, 5, 6, 7, 8]
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const setMoney = (r: any) => moneyCols.forEach(c => { r.getCell(c).numFmt = MONEY })

      for (const r of rows) {
        const sum = ws.addRow([r.product_name, r.sku ?? '', r.qty, r.gross, r.discount, r.net, r.vat, r.total_incl, ''])
        sum.font = { bold: true }
        setMoney(sum)
        for (const d of byProduct.get(r.product_id) ?? []) {
          const dr = ws.addRow([
            fmtDateTime(d.occurred_at), d.bought_from, d.qty, d.unit_price ?? '', d.discount,
            d.net, d.vat, d.total_incl, d.reference ?? '',
          ])
          dr.outlineLevel = 1
          dr.hidden = true
          setMoney(dr)
        }
      }
      if (totals) {
        const t = ws.addRow(['Totals', '', totals.qty, totals.gross, totals.discount, totals.net, totals.vat, totals.total_incl, ''])
        t.font = { bold: true }
        setMoney(t)
      }
      const widths = [28, 22, 10, 16, 12, 14, 12, 15, 16]
      widths.forEach((w, i) => { ws.getColumn(i + 1).width = w })

      const buf = await wb.xlsx.writeBuffer()
      downloadBlob(
        new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        `product-purchases-with-suppliers-${range.from}_to_${range.to}.xlsx`,
      )
    } catch (e) {
      toast.error('Could not build the file: ' + (e instanceof Error ? e.message : String(e)))
    } finally {
      setDownloading(false)
    }
  }

  const money = (n: number) => format(n)

  return (
    <div className="space-y-5">
      <PageHeader title="Product Purchases" subtitle="What you bought, per product. Tap a row for each purchase." />

      {/* Controls */}
      <div className="card p-4 space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-1">
            {PRESETS.map(p => (
              <button
                key={p.value} type="button" onClick={() => setPreset(p.value)}
                className={cn('px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                  preset === p.value ? 'bg-brand-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')}
              >{p.label}</button>
            ))}
          </div>
          {preset === 'custom' && (
            <div className="flex flex-wrap items-center gap-2">
              <input type="date" className="input py-1.5 w-auto" aria-label="From date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} />
              <span className="text-gray-400 text-sm">to</span>
              <input type="date" className="input py-1.5 w-auto" aria-label="To date" value={customTo} onChange={e => setCustomTo(e.target.value)} />
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input className="input pl-9 py-1.5" placeholder="Search product or SKU…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={exportCsv} disabled={!rows.length} className="btn-secondary inline-flex items-center gap-2 disabled:opacity-50">
              <Download className="h-4 w-4" /> Export CSV
            </button>
            <button type="button" onClick={exportWithSuppliers} disabled={!rows.length || downloading}
              title="Excel file with each product expandable (+/−) to its purchase breakdown"
              className="btn-secondary inline-flex items-center gap-2 disabled:opacity-50">
              <Download className="h-4 w-4" /> {downloading ? 'Preparing…' : 'Download with suppliers'}
            </button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : isError ? (
        <EmptyState title="Couldn't load the report" description="Please try again in a moment." />
      ) : !(data?.rows ?? []).length ? (
        <EmptyState title="No purchases in this period" description="Pick a different date range to see product purchases." />
      ) : (
        <>
          {/* KPI tiles */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Tile label="Total spend (net)" value={money(kpis.spend)} />
            <Tile label="Qty purchased" value={kpis.qty.toLocaleString()} />
            <Tile label="Products" value={kpis.products.toLocaleString()} />
            <Tile label="Suppliers" value={kpis.suppliers.toLocaleString()} />
          </div>

          {/* Daily spend trend */}
          <div className="card p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Daily spend</h3>
              <span className="text-xs text-gray-400">{trend.length} day{trend.length === 1 ? '' : 's'}</span>
            </div>
            {trend.length ? (
              <div className="flex items-end gap-0.5 h-14 overflow-x-auto">
                {trend.map(d => (
                  <div key={d.day} className="flex-1 min-w-[3px] rounded-t bg-brand-400/70 hover:bg-brand-500 transition-colors"
                    style={{ height: `${Math.max(2, (Math.abs(d.net) / trendMax) * 100)}%` }}
                    title={`${displayDate(d.day)} — ${money(d.net)}`} />
                ))}
              </div>
            ) : <p className="text-sm text-gray-400">No purchases.</p>}
          </div>

          {/* Top products / suppliers */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <MiniCard title="Top products by spend">
              {topProducts.map(p => (
                <MiniRow key={p.product_id} label={p.product_name} sub={`${p.qty.toLocaleString()} qty`} value={money(p.net)} />
              ))}
            </MiniCard>
            <MiniCard title="Top suppliers by spend">
              {topSuppliers.length
                ? topSuppliers.map(s => <MiniRow key={s.name} label={s.name} sub="" value={money(s.net)} />)
                : <p className="text-sm text-gray-400">No suppliers.</p>}
            </MiniCard>
          </div>

          {/* Detail table */}
          <div className="card p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-500 border-b border-gray-200">
                    <th className="px-3 py-2 text-left font-medium">Product</th>
                    <SortableTh label="Qty" k="qty" sort={sort} onSort={toggleSort} />
                    <th className="px-3 py-2 text-right font-medium">Gross cost</th>
                    <th className="px-3 py-2 text-right font-medium">Discount</th>
                    <SortableTh label="Net cost" k="net" sort={sort} onSort={toggleSort} />
                    <th className="px-3 py-2 text-right font-medium">VAT</th>
                    <th className="px-3 py-2 text-right font-medium">Total incl VAT</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map(r => {
                    const open = expanded === r.product_id
                    return (
                      <Fragment key={r.product_id}>
                        <tr onClick={() => setExpanded(open ? null : r.product_id)} className="cursor-pointer hover:bg-gray-50">
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-2">
                              {open ? <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" /> : <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />}
                              <div className="min-w-0">
                                <div className="font-medium text-gray-900 truncate">{r.product_name}</div>
                                {r.sku && <div className="text-xs text-gray-400 truncate">{r.sku}</div>}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums">{r.qty.toLocaleString()}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums">{money(r.gross)}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-gray-500">{r.discount ? `−${money(r.discount)}` : money(0)}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums font-medium">{money(r.net)}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-gray-500">{money(r.vat)}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums">{money(r.total_incl)}</td>
                        </tr>
                        {open && (
                          <tr key={r.product_id + '-detail'}>
                            <td colSpan={7} className="bg-gray-50 px-3 py-2">
                              <ProductPurchaseDetail tenantId={tenantId} productId={r.product_id} from={range.from} to={range.to} money={money} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    )
                  })}
                </tbody>
                {totals && (
                  <tfoot>
                    <tr className="border-t-2 border-gray-200 font-semibold text-gray-900 bg-gray-50/60">
                      <td className="px-3 py-3">Totals</td>
                      <td className="px-3 py-3 text-right tabular-nums">{totals.qty.toLocaleString()}</td>
                      <td className="px-3 py-3 text-right tabular-nums">{money(totals.gross)}</td>
                      <td className="px-3 py-3 text-right tabular-nums">{totals.discount ? `−${money(totals.discount)}` : money(0)}</td>
                      <td className="px-3 py-3 text-right tabular-nums">{money(totals.net)}</td>
                      <td className="px-3 py-3 text-right tabular-nums">{money(totals.vat)}</td>
                      <td className="px-3 py-3 text-right tabular-nums">{money(totals.total_incl)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="card p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-400">{label}</p>
      <p className="mt-1 text-xl font-semibold text-gray-900 tabular-nums">{value}</p>
    </div>
  )
}
function MiniCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card p-4">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">{title}</h3>
      <div className="space-y-1.5">{children}</div>
    </div>
  )
}
function MiniRow({ label, sub, value }: { label: string; sub: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-2 text-sm">
      <div className="min-w-0">
        <div className="truncate text-gray-800">{label}</div>
        {sub && <div className="text-xs text-gray-400">{sub}</div>}
      </div>
      <div className="tabular-nums font-medium text-gray-900 shrink-0">{value}</div>
    </div>
  )
}
function SortableTh({ label, k, sort, onSort }: {
  label: string; k: SortKey; sort: { key: SortKey; dir: 'asc' | 'desc' }; onSort: (k: SortKey) => void
}) {
  const active = sort.key === k
  return (
    <th className="px-3 py-2 text-right font-medium">
      <button type="button" onClick={() => onSort(k)} className={cn('inline-flex items-center gap-1 hover:text-gray-900', active && 'text-gray-900')}>
        {label}{active && <span className="text-[10px]">{sort.dir === 'desc' ? '▼' : '▲'}</span>}
      </button>
    </th>
  )
}

function ProductPurchaseDetail({ tenantId, productId, from, to, money }: {
  tenantId: string; productId: string; from: string; to: string; money: (n: number) => string
}) {
  const { data, isLoading } = useQuery<{ rows: DetailRow[] }>({
    queryKey: ['product-purchase-detail', tenantId, productId, from, to],
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await rpc('get_product_purchase_detail', {
        p_tenant_id: tenantId, p_product_id: productId, p_from: from, p_to: to,
      })
      if (error) throw error
      return data as { rows: DetailRow[] }
    },
  })
  if (isLoading) return <div className="py-3 flex justify-center"><Spinner /></div>
  const rows = data?.rows ?? []
  if (!rows.length) return <div className="py-2 text-xs text-gray-500">No purchase detail in this period.</div>
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-gray-400 border-b border-gray-200">
            <th className="px-2 py-1.5 text-left font-medium">Date</th>
            <th className="px-2 py-1.5 text-left font-medium">Bought from</th>
            <th className="px-2 py-1.5 text-right font-medium">Qty</th>
            <th className="px-2 py-1.5 text-right font-medium">Unit cost</th>
            <th className="px-2 py-1.5 text-right font-medium">Discount</th>
            <th className="px-2 py-1.5 text-right font-medium">Net</th>
            <th className="px-2 py-1.5 text-right font-medium">VAT</th>
            <th className="px-2 py-1.5 text-right font-medium">Total incl VAT</th>
            <th className="px-2 py-1.5 text-left font-medium">Ref</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((d, i) => {
            const isReturn = d.kind === 'return'
            return (
              <tr key={i} className={cn(isReturn && 'text-red-600')}>
                <td className="px-2 py-1.5 whitespace-nowrap">{fmtDateTime(d.occurred_at)}</td>
                <td className="px-2 py-1.5">
                  <span className="inline-flex items-center gap-1">
                    {d.supplier_id
                      ? <Link to={`/contacts/suppliers/${d.supplier_id}/ledger`} className="text-brand-600 hover:underline" onClick={e => e.stopPropagation()}>{d.bought_from}</Link>
                      : d.bought_from}
                    {isReturn && <span className="badge badge-red">Return</span>}
                  </span>
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums">{d.qty.toLocaleString()}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{d.unit_price === null ? '—' : money(d.unit_price)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{d.discount ? `−${money(d.discount)}` : money(0)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{money(d.net)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{money(d.vat)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{money(d.total_incl)}</td>
                <td className="px-2 py-1.5 whitespace-nowrap text-gray-500">
                  {d.reference
                    ? (d.purchase_id
                        ? <Link to={`/purchases/${d.purchase_id}`} className="text-brand-600 hover:underline" onClick={e => e.stopPropagation()}>{d.reference}</Link>
                        : d.reference)
                    : '—'}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
